import { IAppContext } from 'App/AppContext';
import { mapFromControl } from 'mappings/controlMappings';
import {
  mapFromISOControl,
  mapFromISOControlISOControl,
  mapFromISOControls,
  mapFromISOControlTransMapping,
  mapToISOControl,
} from 'mappings/isoControlMapping';
import { mapFromISONorm, mapFromISONorms, mapToISONorm } from 'mappings/normMapping';
import Control, { ControlTypes } from 'models/control';
import ControlDTO from 'models/dto/controlDTO';
import ControlISOControlDTO from 'models/dto/controlISOControlDTO';
import ISOControlDTO, {
  ISOControlISOControlDTO,
  ISOControlTransMappingDTO,
  ISONormUpgradeCheckMapDTO,
} from 'models/dto/isoControlDTO';
import { ISOControlInputDTO } from 'models/dto/isoControlInputDTO';
import { ISONormDTO } from 'models/dto/normDTO';
import Entity, { EntityTypes } from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import ISOControl, { ISOControlISOControl, ISOControlTransMapping } from 'models/isoControl';
import { ISONorm } from 'models/norm';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { getEntityUrl, getExternalUrl } from 'utils/url';
import http from './httpService';

export async function apiGetISOControlReference(id: number, accessToken: string, globalDataCache: GlobalDataCache): Promise<ISOControl> {
  try {
    const result = await http.get<ISOControlDTO>(`/iso/controls/reference/${id}`, http.getRequestConfig(accessToken));

    return mapFromISOControl(result.data, globalDataCache);
  } catch (error) {
    throw AppError.fromApiError(error);
  }
}

export async function apiGetISOControls(accessToken: string, globalDataCache: GlobalDataCache): Promise<ISOControl[]> {
  try {
    const ar = await http.get<ISOControlDTO[]>(`iso/controls`, http.getRequestConfig(accessToken));

    return mapFromISOControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAllISOControls(
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ISOControl[]> {
  try {
    const ar = await http.get<ISOControlDTO[]>(`iso/controls/all`, http.getRequestConfig(accessToken));

    return mapFromISOControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSetISOControl(
  isoControlId: number,
  controlId: number,
  add: boolean,
  accessToken: string,
): Promise<void> {
  try {
    const controlISOControlDTO: ControlISOControlDTO = new ControlISOControlDTO(isoControlId, controlId);
    await http.post(`/controls/iso/?add=${add}`, controlISOControlDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiTransferISONorm(
  isoNormId: number,
  createControls: boolean,
  langCode: string,
  authSchemaId: number | undefined,
  accessToken: string,
): Promise<void> {
  try {
    let url = `iso/transfer/${isoNormId}/${langCode}?createControls=${createControls}`;
    if (authSchemaId) {
      url += `&authSchemaId=${authSchemaId}`;
    }

    await http.post(url, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetISONorms(includeNotPublished: boolean, accessToken: string): Promise<ISONorm[]> {
  try {
    const ar = await http.get<ISONormDTO[]>(
      `/iso/norm?includeNotPublished=${includeNotPublished}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromISONorms(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetISOControlRelations(
  isoControlId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ISOControl[]> {
  try {
    const ar = await http.get<ISOControlDTO[]>(
      `iso/controls/${isoControlId}/relations`,
      http.getRequestConfig(accessToken),
    );

    return mapFromISOControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddISOControlRelation(
  isoControlIdFrom: number,
  isoControlIdTo: number,
  accessToken: string,
): Promise<ISOControlISOControl | undefined> {
  try {
    const newISOControlISOControlDTO = new ISOControlISOControlDTO();
    newISOControlISOControlDTO.isoControlIdFrom = isoControlIdFrom;
    newISOControlISOControlDTO.isoControlIdTo = isoControlIdTo;

    const ar = await http.post<ISOControlISOControlDTO>(
      `iso/controls/relations`,
      newISOControlISOControlDTO,
      http.getRequestConfig(accessToken),
    );

    if (ar && ar.data) {
      return mapFromISOControlISOControl(ar.data);
    } else {
      return undefined;
    }
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateISOControlRelation(relation: ISOControlISOControl, accessToken: string): Promise<void> {
  try {
    const newISOControlISOControlDTO = new ISOControlISOControlDTO();
    newISOControlISOControlDTO.isoControlIdFrom = relation.isoControlIdFrom;
    newISOControlISOControlDTO.isoControlIdTo = relation.isoControlIdTo;
    newISOControlISOControlDTO.status = relation.status;

    await http.put<ISOControlISOControlDTO>(
      `iso/controls/relations`,
      newISOControlISOControlDTO,
      http.getRequestConfig(accessToken),
    );

    return;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteISOControlRelation(
  isoControlIdFrom: number,
  isoControlIdTo: number,
  accessToken: string,
): Promise<void> {
  try {
    const newISOControlISOControlDTO = new ISOControlISOControlDTO();
    newISOControlISOControlDTO.isoControlIdFrom = isoControlIdFrom;
    newISOControlISOControlDTO.isoControlIdTo = isoControlIdTo;

    await http.post<ISOControlISOControlDTO>(
      `iso/controls/relations/delete`,
      newISOControlISOControlDTO,
      http.getRequestConfig(accessToken),
    );

    return;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetISOControlInputs(isoNormId: number, accessToken: string): Promise<ISOControlInputDTO[]> {
  try {
    const ar = await http.get<ISOControlInputDTO[]>(
      `iso/controls/inputs/${isoNormId}`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddISOControlInput(
  input: ISOControlInputDTO,
  accessToken: string,
): Promise<ISOControlInputDTO> {
  try {
    const ar = await http.post<ISOControlInputDTO>(`iso/controls/inputs`, input, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteISOControlInputs(inputs: ISOControlInputDTO[], accessToken: string): Promise<void> {
  try {
    await http.post<ISOControlInputDTO[]>(`iso/controls/inputs/delete`, inputs, http.getRequestConfig(accessToken));

    return;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCheckStandardUpgrade(
  sourceStandard: ISONorm,
  targetStandard: ISONorm,
  standardToCheck: ISONorm,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<ISOControl[]> {
  try {
    const map: ISONormUpgradeCheckMapDTO = {
      sourceISONormId: sourceStandard.isoNormId,
      targetISONormId: targetStandard.isoNormId,
      checkISONormId: standardToCheck.isoNormId,
    };

    const ar = await http.post<ISOControlDTO[]>(`/iso/upgrade/check`, map, http.getRequestConfig(accessToken));

    return mapFromISOControls(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetISOControlTransMapping(
  sourceStandard: ISONorm,
  accessToken: string,
): Promise<ISOControlTransMapping[]> {
  try {
    const ar = await http.get<ISOControlTransMappingDTO[]>(
      `/iso/upgrade/trans/${sourceStandard.isoNormId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromISOControlTransMapping(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateISOControls(
  isoNormId: number,
  languageCode: string,
  accessToken: string,
): Promise<void> {
  try {
    await http.post(`/iso/${isoNormId}/${languageCode}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetISOControlsForLang(
  isoNormId: number,
  languageId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ISOControl[]> {
  try {
    const ar = await http.get<ISOControlDTO[]>(
      `/iso/controls/${isoNormId}/${languageId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromISOControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetISOControlsForStandard(
  isoNormId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ISOControl[]> {
  try {
    const ar = await http.get<ISOControlDTO[]>(`/iso/controls/${isoNormId}`, http.getRequestConfig(accessToken));

    return mapFromISOControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddISOControl(
  isoControl: ISOControl,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ISOControl> {
  try {
    const isoControlDTO: ISOControlDTO = mapToISOControl(isoControl);
    const ar = await http.post('/iso/controls', isoControlDTO, http.getRequestConfig(accessToken));

    return mapFromISOControl(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateISOControl(isoControl: ISOControl, accessToken: string): Promise<void> {
  try {
    const isoControlDTO: ISOControlDTO = mapToISOControl(isoControl);
    await http.put('/iso/controls', isoControlDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteISOControl(isoControl: ISOControl, accessToken: string): Promise<void> {
  try {
    const isoControlDTO: ISOControlDTO = mapToISOControl(isoControl);
    await http.post('/iso/controls/delete', isoControlDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopyISONorm(
  isoNorm: ISONorm,
  targetLangCode: string | undefined,
  accessToken: string,
): Promise<ISONorm> {
  try {
    const isoNormDTO = mapToISONorm(isoNorm);
    let ar;

    if (targetLangCode) {
      ar = await http.post(
        `/iso/norm/copy?targetLang=${targetLangCode}`,
        isoNormDTO,
        http.getRequestConfig(accessToken),
      );
    } else {
      ar = await http.post(`/iso/norm/copy`, isoNormDTO, http.getRequestConfig(accessToken));
    }

    return mapFromISONorm(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateISONorm(isoNorm: ISONorm, accessToken: string): Promise<void> {
  try {
    const isoNormDTO = mapToISONorm(isoNorm);
    await http.put<ISONormDTO[]>(`/iso/norm`, isoNormDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteISONorm(isoNorm: ISONorm, targetLangCode: string, accessToken: string): Promise<void> {
  try {
    await http.post<ISONormDTO[]>(
      `/iso/norm/delete/${isoNorm.isoNormId}?targetLang=${targetLangCode}`,
      null,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlForISOControl(
  isoControlId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Control | undefined> {
  try {
    const ar = await http.get<ControlDTO | undefined>(
      `/iso/controls/${isoControlId}/control`,
      http.getRequestConfig(accessToken),
    );

    return mapFromControl(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Portable standard references: `isocontrol://{id}` anchors in package HTML, and what the app does with one.

export const parseISOControlReference = (href: string | null | undefined): number | undefined => {
  const match = /^isocontrol:\/\/([1-9]\d*)$/i.exec(href ?? '');
  const id = match ? Number(match[1]) : undefined;

  return id !== undefined && Number.isSafeInteger(id) && id <= 2147483647 ? id : undefined;
};

export const createISOControlReference = (id: number, label: string): string => {
  if (parseISOControlReference(`isocontrol://${id}`) === undefined) throw new Error('Invalid ISO control ID');
  const anchor = document.createElement('a');
  anchor.href = `isocontrol://${id}`;
  anchor.textContent = label;

  return anchor.outerHTML;
};

export const loadISOControlReference = async (app: IAppContext, id: number): Promise<ISOControl> => {
  const token = await app.getAccessToken(apiRequest.scopes);

  return apiGetISOControlReference(id, token, app.globalDataCache);
};

export const getISOControlReferenceUrl = (app: IAppContext, id: number): string =>
  getExternalUrl(`/references/isocontrol/${id}`, app.user.tenant, app.user.login.tenantId);

// Maps an ISO control onto the tenant's own requirement or control. Returns undefined for every failure.
export const resolveISOControlReferenceUrl = async (app: IAppContext, id: number): Promise<string | undefined> => {
  try {
    const token = await app.getAccessToken(apiRequest.scopes);
    const control = await apiGetControlForISOControl(id, token, app.globalDataCache);
    if (!control) return undefined;
    const entity = new Entity(
      control.controlId,
      control.controlType === ControlTypes.Measure ? EntityTypes.Control : EntityTypes.Requirement,
      control.name,
      control.code,
    );

    return getEntityUrl(entity) || undefined;
  } catch {
    return undefined;
  }
};
